import { useCallback } from "react";
import Dexie, { Table } from "dexie";
import { useLiveQuery } from "dexie-react-hooks";

export interface Download {
  id?: number;
  name: string;
  createdAt: Date;
  tileCount: number;
  extent: [number, number, number, number];
  startZoom: number;
  endZoom: number;
  status: "created" | "downloaded";
}
export interface Tile {
  downloadId: number;
  x: number;
  y: number;
  z: number;
  data: Blob;
}

class OfflineMapsDatabase extends Dexie {
  downloads!: Table<Download, number>;
  tiles!: Table<Tile>;

  constructor() {
    super("piano-offline-maps");
    this.version(1).stores({
      downloads: "++id, status",
      tiles: "[x+y+z+downloadId]",
    });
  }
}
const db = new OfflineMapsDatabase();

export function useOfflineMaps() {
  const downloads = useLiveQuery(() => db.downloads.toArray());

  const addDownload = useCallback(
    (download: Omit<Download, "id" | "createdAt" | "status">) =>
      db.downloads.add({ ...download, createdAt: new Date(), status: "created" }),
    []
  );
  const remove = useCallback((id: number) => db.downloads.delete(id), []);

  const setDownloadStatus = useCallback((id: number, status: Download["status"]) => db.downloads.update(id, { status }), []);
  const addTiles = useCallback((tiles: Tile[]) => db.tiles.bulkAdd(tiles), []);

  return { downloads: downloads || [], addDownload, remove, setDownloadStatus, addTiles };
}

export async function makeOfflineTileSource(id: number) {
  const download = await db.downloads.get(id);
  if (!download) {
    throw new Error("Download does not exist");
  }
  return new ol.source.XYZ({
    maxZoom: download.endZoom,
    minZoom: download.startZoom,
    url: "local://{z}/{x}/{y}.png", // not actually used, but required for the load function to be called
    tileLoadFunction: async (tile) => {
      const [z, x, y] = tile.getTileCoord();
      const data = await db.tiles.get({ downloadId: id, x, y: -y - 1, z });
      if (!data) {
        return;
      }
      // TODO (on openlayers upgrade): it seems ol.Tile.getImage does not exist on our version of openlayers, this seems to work though?
      (tile as any).getImage().src = URL.createObjectURL(data.data);
    },
  });
}
